import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Wallet = ({ isRTL, onBack, token, API }) => {
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, purchases, subscriptions, gifts

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/wallet/summary`, { headers });
      setWalletData(response.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
      alert(isRTL ? 'خطأ في تحميل المحفظة' : 'Error loading wallet');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (!walletData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center">
          <p className="text-gray-600">{isRTL ? 'خطأ في تحميل البيانات' : 'Error loading data'}</p>
          <button onClick={onBack} className="mt-4 text-purple-600 hover:underline">
            {isRTL ? 'رجوع' : 'Back'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700"
          >
            <svg className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {isRTL ? 'رجوع' : 'Back'}
          </button>
          <h1 className="text-2xl font-bold text-purple-600 flex items-center gap-2">
            <span>👛</span>
            {isRTL ? 'محفظتي' : 'My Wallet'}
          </h1>
          <div className="w-16"></div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Balance Card */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-purple-100 text-sm mb-2">{isRTL ? 'الرصيد الحالي' : 'Current Balance'}</p>
              <h2 className="text-5xl font-bold flex items-center gap-3">
                <span>💎</span>
                <span>{walletData.current_balance.toLocaleString()}</span>
              </h2>
            </div>
            <div className="w-24 h-24 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-5xl">
              💰
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white border-opacity-30">
            <div>
              <p className="text-purple-100 text-xs mb-1">{isRTL ? 'مجموع المشتريات' : 'Total Purchased'}</p>
              <p className="text-xl font-bold">💎 {walletData.total_purchased.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-purple-100 text-xs mb-1">{isRTL ? 'مجموع المصروفات' : 'Total Spent'}</p>
              <p className="text-xl font-bold">💎 {walletData.total_spent.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-purple-100 text-xs mb-1">{isRTL ? 'الهدايا المستلمة' : 'Gifts Received'}</p>
              <p className="text-xl font-bold">🎁 {walletData.total_received_gifts}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-6">
          <div className="flex border-b overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'overview'
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                  : 'text-gray-600 hover:text-purple-600'
              }`}
            >
              {isRTL ? '📊 نظرة عامة' : '📊 Overview'}
            </button>
            <button
              onClick={() => setActiveTab('purchases')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'purchases'
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                  : 'text-gray-600 hover:text-purple-600'
              }`}
            >
              {isRTL ? '🛒 المشتريات' : '🛒 Purchases'}
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'subscriptions'
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                  : 'text-gray-600 hover:text-purple-600'
              }`}
            >
              {isRTL ? '📝 الاشتراكات' : '📝 Subscriptions'}
            </button>
            <button
              onClick={() => setActiveTab('gifts')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'gifts'
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                  : 'text-gray-600 hover:text-purple-600'
              }`}
            >
              {isRTL ? '🎁 الهدايا' : '🎁 Gifts'}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {isRTL ? 'ملخص المحفظة' : 'Wallet Summary'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-700 mb-1">{isRTL ? 'الاشتراكات النشطة' : 'Active Subscriptions'}</p>
                      <p className="text-2xl font-bold text-green-800">
                        {walletData.subscriptions.filter(s => s.subscription_status === 'active').length}
                      </p>
                    </div>
                    <div className="text-4xl">📝</div>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-700 mb-1">{isRTL ? 'الجواهر المصروفة على الاشتراكات' : 'Gems Spent on Subscriptions'}</p>
                      <p className="text-2xl font-bold text-blue-800">
                        💎 {walletData.total_spent_subscriptions.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-4xl">📊</div>
                  </div>
                </div>

                <div className="bg-pink-50 border-l-4 border-pink-500 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-pink-700 mb-1">{isRTL ? 'الهدايا المرسلة' : 'Gifts Sent'}</p>
                      <p className="text-2xl font-bold text-pink-800">
                        {walletData.gifts_sent.length}
                      </p>
                    </div>
                    <div className="text-4xl">🎁</div>
                  </div>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-700 mb-1">{isRTL ? 'الجواهر المصروفة على الهدايا' : 'Gems Spent on Gifts'}</p>
                      <p className="text-2xl font-bold text-purple-800">
                        💎 {walletData.total_spent_gifts.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-4xl">💸</div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="mt-8">
                <h4 className="text-lg font-bold text-gray-800 mb-4">
                  {isRTL ? 'آخر المدفوعات' : 'Recent Payments'}
                </h4>
                {walletData.payments.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    {isRTL ? 'لا توجد مدفوعات بعد' : 'No payments yet'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {walletData.payments.slice(0, 5).map((payment, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-xl">
                            💎
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">
                              {isRTL ? 'دفعة اشتراك' : 'Subscription Payment'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(payment.payment_date).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-red-600">-{payment.amount_gems} 💎</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Purchases Tab */}
          {activeTab === 'purchases' && (
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {isRTL ? 'سجل الشراء' : 'Purchase History'}
              </h3>
              {walletData.purchases.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🛒</div>
                  <p className="text-gray-500">{isRTL ? 'لم تقم بشراء جواهر بعد' : 'No purchases yet'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {walletData.purchases.map((purchase, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-2xl">
                          💰
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">
                            {isRTL ? 'شراء جواهر' : 'Gems Purchase'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(purchase.created_at).toLocaleString(isRTL ? 'ar-SA' : 'en-US')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">
                          +{purchase.gems_amount} 💎
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Subscriptions Tab */}
          {activeTab === 'subscriptions' && (
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {isRTL ? 'اشتراكاتي' : 'My Subscriptions'}
              </h3>
              {walletData.subscriptions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📝</div>
                  <p className="text-gray-500">{isRTL ? 'لا توجد اشتراكات' : 'No subscriptions'}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {walletData.subscriptions.map((sub, index) => (
                    <div key={index} className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border-2 border-purple-200">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-gray-800 mb-2">
                            {sub.room_name}
                          </h4>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              sub.subscription_status === 'active'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {sub.subscription_status === 'active' 
                                ? (isRTL ? '🟢 نشط' : '🟢 Active')
                                : (isRTL ? '🔴 رصيد غير كافٍ' : '🔴 Insufficient Funds')
                              }
                            </span>
                            <span className="text-xs text-gray-600">
                              {isRTL ? `حالة الغرفة: ${sub.room_status}` : `Room Status: ${sub.room_status}`}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">{isRTL ? 'المساهمة اليومية' : 'Daily Amount'}</p>
                          <p className="text-lg font-bold text-purple-600">
                            {sub.daily_gems_amount} 💎
                          </p>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">{isRTL ? 'عدد الدفعات' : 'Payments Made'}</p>
                          <p className="text-lg font-bold text-blue-600">
                            {sub.total_payments_made}
                          </p>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">{isRTL ? 'إجمالي المدفوع' : 'Total Paid'}</p>
                          <p className="text-lg font-bold text-green-600">
                            {sub.total_paid} 💎
                          </p>
                        </div>
                      </div>

                      {sub.missed_payments > 0 && (
                        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-sm text-red-700">
                            ⚠️ {isRTL ? `دفعات فائتة: ${sub.missed_payments}` : `Missed Payments: ${sub.missed_payments}`}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Gifts Tab */}
          {activeTab === 'gifts' && (
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                {isRTL ? 'سجل الهدايا' : 'Gifts History'}
              </h3>

              {/* Gifts Sent */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span>📤</span>
                  {isRTL ? 'الهدايا المرسلة' : 'Gifts Sent'}
                  <span className="text-sm text-gray-500">({walletData.gifts_sent.length})</span>
                </h4>
                {walletData.gifts_sent.length === 0 ? (
                  <p className="text-center text-gray-500 py-6 bg-gray-50 rounded-lg">
                    {isRTL ? 'لم ترسل أي هدايا بعد' : 'No gifts sent yet'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {walletData.gifts_sent.map((gift, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-pink-50 rounded-lg border border-pink-200">
                        <div className="flex items-center gap-3">
                          <div className="text-3xl">{gift.gift_emoji}</div>
                          <div>
                            <p className="font-semibold text-gray-800">
                              {isRTL ? gift.gift_name_ar : gift.gift_name_en}
                            </p>
                            <p className="text-sm text-gray-600">
                              {isRTL ? 'إلى:' : 'To:'} {gift.receiver_name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(gift.created_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-red-600">-{gift.gems_cost} 💎</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Gifts Received */}
              <div>
                <h4 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span>📥</span>
                  {isRTL ? 'الهدايا المستلمة' : 'Gifts Received'}
                  <span className="text-sm text-gray-500">({walletData.gifts_received.length})</span>
                </h4>
                {walletData.gifts_received.length === 0 ? (
                  <p className="text-center text-gray-500 py-6 bg-gray-50 rounded-lg">
                    {isRTL ? 'لم تستلم أي هدايا بعد' : 'No gifts received yet'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {walletData.gifts_received.map((gift, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-3">
                          <div className="text-3xl">{gift.gift_emoji}</div>
                          <div>
                            <p className="font-semibold text-gray-800">
                              {isRTL ? gift.gift_name_ar : gift.gift_name_en}
                            </p>
                            <p className="text-sm text-gray-600">
                              {isRTL ? 'من:' : 'From:'} {gift.sender_name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(gift.created_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">{gift.gems_cost} 💎</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Wallet;
